import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  PeopleAlt as PeopleAltIcon,
  QueryStats as QueryStatsIcon,
  Schedule as ScheduleIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';

import StatCard from './StatCard';
import DateRangePopover from './DateRangePopover';
import GenderCard from './GenderCard';
import AgeCard from './AgeCard';
import VegaTrafficChart from './VegaTrafficChart';
import useStoreStats from '../../Context/Hooks/useStoreStats';
import { toUtcStartOfDay, toUtcEndOfDay } from '../../Utils/format';

const PERIODS = ['today', '7d', '30d', 'month', 'custom'];

const periodToLabel = (p, t) => {
  const map = { today: 'today', '7d': 'sevenDays', '30d': 'thirtyDays', month: 'month', custom: 'custom' };
  return t(`dashboard:filters.${map[p]}`);
};

const buildDateRange = (period) => {
  const now = new Date();
  const start = new Date();
  if (period === 'today') {
    start.setHours(0, 0, 0, 0);
  } else if (period === '7d') {
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  } else if (period === '30d') {
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  } else if (period === 'month') {
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
  } else if (period === 'custom') {
    start.setDate(start.getDate() - 1);
    start.setHours(0, 0, 0, 0);
    return { start: toUtcStartOfDay(start), end: toUtcEndOfDay(now) };
  }
  return { start: toUtcStartOfDay(start), end: now };
};

// eslint-disable-next-line react/prop-types
const PeriodToggle = ({ value, onChange, t }) => (
  <ToggleButtonGroup
    value={value}
    exclusive
    onChange={(_, v) => v && onChange(v)}
    size="small"
    sx={{
      '& .MuiToggleButton-root': {
        px: 1.5,
        py: 0.5,
        fontSize: '0.8125rem',
        fontWeight: 500,
        textTransform: 'none',
        border: '1px solid',
        borderColor: 'divider',
        color: 'text.secondary',
        '&.Mui-selected': {
          bgcolor: 'primary.dark',
          color: '#fff',
          borderColor: 'primary.dark',
          '&:hover': { bgcolor: 'primary.main' },
        },
      },
    }}
  >
    {PERIODS.map(p => (
      <ToggleButton key={p} value={p}>{periodToLabel(p, t)}</ToggleButton>
    ))}
  </ToggleButtonGroup>
);

const StoreStatsSection = ({ clientId, storeId, devices = [] }) => {
  const { t } = useTranslation();

  const [period, setPeriod] = useState('today');
  const [startDate, setStartDate] = useState(() => buildDateRange('today').start);
  const [endDate, setEndDate] = useState(() => buildDateRange('today').end);
  const [granularity, setGranularity] = useState('HOUR');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState([]);

  useEffect(() => {
    if (!period) return;
    const { start, end } = buildDateRange(period);
    setStartDate(start);
    setEndDate(end);
  }, [period]);

  const deviceIds = useMemo(
    () => (selectedDeviceIds.length > 0 ? selectedDeviceIds : null),
    [selectedDeviceIds],
  );

  const sortedDevices = useMemo(() => {
    if (!Array.isArray(devices)) {
      return [];
    }
    return [...devices].sort((a, b) => {
      const aCurrent = a.isCurrent !== false;
      const bCurrent = b.isCurrent !== false;
      if (aCurrent && !bCurrent) {
        return -1;
      }
      if (!aCurrent && bCurrent) {
        return 1;
      }
      return (a.name || a.externalId || '').localeCompare(b.name || b.externalId || '');
    });
  }, [devices]);

  const {
    isLoading,
    visitorsToday,
    visitorsTotal,
    todayTrend,
    last7Days,
    weekTrend,
    sparkline7d,
    periodSparkline,
    peakHour,
    peakVisitors,
    dwellFormatted,
    genderSummary,
    ageSummary,
    chartData,
    compareData,
    committedPeriod,
    committedStartDate,
    committedEndDate,
    committedGranularity,
    committedIsToday,
  } = useStoreStats(clientId, storeId, startDate, endDate, period, granularity, deviceIds);

  const visitorsInPeriod = period === 'today' ? visitorsToday : visitorsTotal;
  const visitorsInPeriodTrend = period === 'today' ? todayTrend : weekTrend;
  const trendLabel = period === 'today' ? t('dashboard:kpi.vsYesterday') : t('dashboard:kpi.vsPreviousWeek');

  const handleDeviceChange = (e) => {
    const val = e.target.value;
    setSelectedDeviceIds(typeof val === 'string' ? val.split(',') : val);
  };

  const chartSubtitle = committedIsToday
    ? t('dashboard:chart.hourlySubtitle')
    : t('dashboard:chart.trafficSubtitle');

  return (
    <Box sx={{ position: 'relative' }}>
      {isLoading && (
        <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, width: '100%', borderRadius: 1 }} />
      )}

      {/* ── Section header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2, pt: isLoading ? 0.5 : 0 }}>
        <Typography variant="h4">{t('dashboard:chart.trafficChart')}</Typography>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <PeriodToggle value={period} onChange={setPeriod} t={t} />
          <DateRangePopover
            startDate={startDate}
            endDate={endDate}
            onStartChange={(d) => { setStartDate(toUtcStartOfDay(d)); setPeriod(null); }}
            onEndChange={(d) => { setEndDate(toUtcEndOfDay(d)); setPeriod(null); }}
            startLabel={t('dashboard:filters.dateStart')}
            endLabel={t('dashboard:filters.dateEnd')}
          />
          {sortedDevices.length > 0 && (
            <Select
              size="small"
              multiple
              value={selectedDeviceIds}
              onChange={handleDeviceChange}
              displayEmpty
              renderValue={selected => (
                selected.length === 0
                  ? <span style={{ opacity: 0.55 }}>{t('clients:storeDetail.statsSection.allDevices')}</span>
                  : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map(id => {
                        const dev = sortedDevices.find(d => d.id === id);
                        return <Chip key={id} label={dev?.name || dev?.externalId || id} size="small" />;
                      })}
                    </Box>
              )}
              sx={{ fontSize: '0.8125rem', minWidth: 160, maxWidth: 280 }}
            >
              {sortedDevices.map((d, index) => {
                const showDivider = index > 0 && sortedDevices[index - 1].isCurrent !== false && d.isCurrent === false;
                return [
                  showDivider && <Divider key={`divider-${d.id}`} sx={{ my: 0.5 }} />,
                  <MenuItem key={d.id} value={d.id} sx={{ fontSize: '0.8125rem' }}>
                    <Checkbox checked={selectedDeviceIds.includes(d.id)} size="small" />
                    <ListItemText
                      primary={d.name || d.externalId}
                      secondary={d.isCurrent === false ? `${d.location ? `${d.location} · ` : ''}(${t('clients:storeDetail.statsSection.historical', 'Histórico')})` : (d.location || null)}
                    />
                  </MenuItem>
                ];
              })}
            </Select>
          )}
        </Stack>
      </Box>

      {/* ── Row 1: 3 KPI cards ── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={period === 'today' ? t('dashboard:kpi.visitorsToday') : t('dashboard:kpi.visitorsInPeriod')}
            value={visitorsInPeriod.toLocaleString()}
            trend={visitorsInPeriodTrend}
            trendLabel={trendLabel}
            sparklineData={periodSparkline}
            icon={<PeopleAltIcon fontSize="inherit" />}
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard:kpi.peakHour')}
            value={peakHour}
            subtitle={`${peakVisitors} ${t('dashboard:kpi.visitors')}`}
            icon={<ScheduleIcon fontSize="inherit" />}
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard:kpi.dwell')}
            value={dwellFormatted}
            icon={<TimerIcon fontSize="inherit" />}
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* ── Row 2: 4 metric cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <GenderCard genderSummary={genderSummary} visitorsInPeriod={visitorsInPeriod} loading={isLoading} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <AgeCard ageSummary={ageSummary} loading={isLoading} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            title={t('dashboard:kpi.last7Days')}
            value={last7Days.toLocaleString()}
            sparklineData={sparkline7d}
            icon={<QueryStatsIcon fontSize="inherit" />}
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* ── Chart ── */}
      <Card>
        <CardContent sx={{ p: 2.5 }}>
          <VegaTrafficChart
            data={chartData}
            compareData={compareData}
            isToday={committedPeriod === 'today'}
            period={committedPeriod}
            startDate={committedStartDate}
            endDate={committedEndDate}
            granularity={committedGranularity}
            onGranularityChange={setGranularity}
            title={t('dashboard:chart.trafficChart')}
            subtitle={chartSubtitle}
            height={400}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

StoreStatsSection.propTypes = {
  clientId: PropTypes.string.isRequired,
  storeId: PropTypes.string,
  devices: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    externalId: PropTypes.string,
    location: PropTypes.string,
    status: PropTypes.string,
  })),
};

StoreStatsSection.defaultProps = {
  storeId: null,
  devices: [],
};

export default StoreStatsSection;
